import axios from 'axios'
import { Message } from 'element-ui'

// 创建axios实例
const service = axios.create({
  baseURL: '/api',
  timeout: 10000
})

// 全局配置
service.config = {
  baseURL: '',
  apiURL: '/api',
  uploadURL: '/api/files/upload'
}

// 重试配置
service.defaults.retry = 2 // 重试次数
service.defaults.retryDelay = 1000 // 重试延迟（毫秒）

// 解析JWT获取过期时间
function getTokenExpiration(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 // 转换为毫秒
  } catch (e) {
    return null
  }
}

// 检查token是否即将过期（提前5分钟检测）
function isTokenExpiringSoon(token, thresholdMinutes = 5) {
  const expiration = getTokenExpiration(token)
  if (!expiration) return true
  const now = Date.now()
  const threshold = thresholdMinutes * 60 * 1000
  return expiration - now < threshold
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 从localStorage获取token
    const token = localStorage.getItem('token')
    if (token) {
      // 检查token是否已过期
      const expiration = getTokenExpiration(token)
      if (expiration && Date.now() >= expiration) {
        // Token已过期，清除并跳转登录页
        Message({
          message: '登录已过期，请重新登录',
          type: 'warning',
          duration: 3000
        })
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        localStorage.removeItem('userRole')
        window.location.href = '/login'
        return Promise.reject(new Error('Token已过期'))
      }

      // 检查token是否即将过期（提前5分钟提醒）
      if (isTokenExpiringSoon(token, 5)) {
        console.warn('Token即将过期，建议重新登录')
      }

      config.headers['Authorization'] = 'Bearer ' + token
    } else {
      // 如果没有token，删除可能存在的Authorization头部
      delete config.headers['Authorization']
    }
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data

    // 如果返回的状态码不是200，则认为是错误
    if (res.success === false) {
      Message({
        message: res.message || '请求失败',
        type: 'error',
        duration: 3000
      })
      return Promise.reject(new Error(res.message || '请求失败'))
    }

    return res
  },
  error => {
    console.error('响应错误:', error)

    const config = error.config

    // 只有幂等的 GET 请求且为网络错误/超时（无服务端响应）时才允许重试，写操作(POST/PUT/DELETE)严禁自动重试
    const method = config && config.method ? config.method.toLowerCase() : 'get'
    const isGetMethod = method === 'get'
    if (!config || !config.retry || !isGetMethod || error.response) {
      return handleError(error)
    }

    // 设置重试计数器
    config.__retryCount = config.__retryCount || 0

    // 检查是否已达到最大重试次数
    if (config.__retryCount >= config.retry) {
      return handleError(error)
    }

    // 增加重试计数
    config.__retryCount += 1

    // 创建新的Promise来处理重试延迟
    const backoff = new Promise(resolve => {
      setTimeout(() => {
        resolve()
      }, config.retryDelay || 1000)
    })

    // 返回重试的Promise
    return backoff.then(() => {
      return service(config)
    })
  }
)

// 错误处理函数
function handleError(error) {
  if (error.response) {
    const responseData = error.response.data

    // 检查是否是TOKEN_EXPIRED错误码
    if (responseData && responseData.code === 'TOKEN_EXPIRED') {
      Message({
        message: responseData.message || '登录已过期，请重新登录',
        type: 'warning',
        duration: 3000
      })
      // 清除token并跳转到登录页
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      localStorage.removeItem('userRole')
      window.location.href = '/login'
      return Promise.reject(error)
    }

    switch (error.response.status) {
      case 401:
        Message({
          message: '未登录或登录已过期，请重新登录',
          type: 'error',
          duration: 3000
        })
        // 清除token并跳转到登录页
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        localStorage.removeItem('userRole')
        window.location.href = '/login'
        break
      case 403:
        Message({
          message: '权限不足，当前账号无此操作权限',
          type: 'error',
          duration: 3000
        })
        break
      case 404:
        Message({
          message: '请求的资源不存在',
          type: 'error',
          duration: 3000
        })
        break
      case 500:
        Message({
          message: '服务器错误',
          type: 'error',
          duration: 3000
        })
        break
      default:
        Message({
          message: error.response.data.message || '请求失败',
          type: 'error',
          duration: 3000
        })
    }
  } else if (error.code === 'ECONNABORTED') {
    Message({
      message: '请求超时，请稍后重试',
      type: 'error',
      duration: 3000
    })
  } else {
    Message({
      message: '网络错误，请检查网络连接',
      type: 'error',
      duration: 3000
    })
  }

  return Promise.reject(error)
}

// 全局图片URL处理函数
service.getFullImageUrl = function (url) {
  // 如果URL为空或undefined，返回默认图片
  if (!url) return '';

  // 如果URL已经是完整路径，则直接返回
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // 如果URL以/uploads开头，则加上基础路径
  if (url.startsWith('/uploads/')) {
    return url;
  }

  // 其他情况返回原URL
  return url;
};

// 全局上传配置函数
service.getUploadConfig = function () {
  return {
    action: service.config.uploadURL,
    headers: {
      'Authorization': 'Bearer ' + localStorage.getItem('token')
    }
  };
};

// 公开上传配置（无需身份验证，用于企业入驻申请等公开页面）
service.getPublicUploadConfig = function () {
  return {
    action: '/api/public/files/upload',
    headers: {}
  };
};

/**
 * 全局下载图片/文件函数
 * @param {string} url 文件URL
 * @param {string} filename 下载保存的文件名
 */
service.downloadImage = async function (url, filename) {
  if (!url) return;
  
  const fullUrl = this.getFullImageUrl(url);
  try {
    const response = await axios({
      url: fullUrl,
      method: 'GET',
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename || url.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('下载文件失败:', error);
    Message.error('下载文件失败');
  }
};

export default service